/**
 * <AdminCommentsPanel> — surfaces client-portal comments on the main-app
 * Incidents / Maintenance / DAR row cards. Admins can also reply directly
 * (their replies are stored with role='admin' so the client can see them
 * marked as internal team responses).
 *
 * Usage:
 *   <AdminCommentsPanel kind="incidents" targetId={i.id} />
 *
 * Renders as a compact toggle button by default; expands inline into a
 * comment thread + reply textarea when clicked.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PillBadge, GhostButton } from "./Primitives";
import { Modal } from "./Modal";
import { MessageSquare, Send, ChevronDown, ChevronRight } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch (err) { return iso; }
}

export function AdminCommentsPanel({ kind, targetId, className = "", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/comments", { params: { kind, target_id: targetId } });
      setRows(Array.isArray(r.data) ? r.data : []);
      setLoaded(true);
    } catch (err) {
      setRows([]);
    } finally { setLoading(false); }
  }, [kind, targetId]);

  // Prefetch the count once on mount so the pill can show "(N)".
  useEffect(() => {
    if (!loaded) load();
  }, [targetId, loaded, load]);

  const clientCount = rows.filter((c) => (c.role || "client") === "client").length;
  const totalCount = rows.length;

  const onPost = async (e) => {
    e.preventDefault();
    const trimmed = reply.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      await api.post("/portal/comments", { kind, target_id: targetId, body: trimmed });
      setReply("");
      await load();
      toast.success("Reply posted");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setPosting(false); }
  };

  return (
    <div className={`mt-3 ${className}`} data-testid={`admin-comments-${kind}-${targetId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest ${
          clientCount > 0 ? "text-[#FF9F0A] hover:text-white" : "text-zinc-500 hover:text-zinc-200"
        }`}
        data-testid={`admin-comments-toggle-${targetId}`}
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        <MessageSquare className="size-3.5" />
        {totalCount === 0
          ? "Discussion"
          : clientCount > 0
            ? `${totalCount} comment${totalCount === 1 ? "" : "s"} · ${clientCount} from client`
            : `${totalCount} comment${totalCount === 1 ? "" : "s"}`}
      </button>

      {open && (
        <div className="mt-3 border-l-2 border-[#27272A] pl-3" data-testid={`admin-comments-panel-${targetId}`}>
          {loading ? (
            <div className="text-xs text-zinc-500 py-2">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-zinc-500 py-2 italic">No comments on this report yet.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((c) => {
                const isAdmin = (c.role || "client") === "admin";
                return (
                  <div key={c.id} className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={`admin-comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-zinc-100 text-sm">{c.user_name}</span>
                      <PillBadge tone={isAdmin ? "blue" : "amber"}>{isAdmin ? "Internal reply" : "Client"}</PillBadge>
                      <span className="text-xs text-zinc-500 font-mono ml-auto">{fmt(c.created_at)}</span>
                    </div>
                    <div className="text-sm text-zinc-200 whitespace-pre-line">{c.body}</div>
                  </div>
                );
              })}
            </div>
          )}

          <form onSubmit={onPost} className="mt-3 space-y-2">
            <textarea
              data-testid={`admin-comment-reply-${targetId}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Reply to the client (visible in their portal)…"
              rows={2}
              maxLength={5000}
              className="w-full text-sm bg-[#0A0A0A] border border-[#27272A] focus:border-[#0A84FF] px-3 py-2 outline-none text-zinc-100 placeholder-zinc-600 resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-500 font-mono">{reply.length} / 5000</div>
              <GhostButton
                type="submit"
                disabled={posting || !reply.trim()}
                data-testid={`admin-comment-post-${targetId}`}
              >
                <Send className="size-3.5" /> {posting ? "Posting…" : "Post reply"}
              </GhostButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


/**
 * <AdminCommentsButton> — compact icon-button variant that opens a modal
 * with the same comments UI. Useful for TABLE layouts (Maintenance, DAR)
 * where inline expansion would break the row grid.
 */
export function AdminCommentsButton({ kind, targetId, targetTitle }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);

  // Prefetch count once so the button can show "N".
  useEffect(() => {
    let cancelled = false;
    api.get("/portal/comments", { params: { kind, target_id: targetId } })
      .then((r) => { if (!cancelled) setCount(Array.isArray(r.data) ? r.data.length : 0); })
      .catch(() => { if (!cancelled) setCount(0); });
    return () => { cancelled = true; };
  }, [kind, targetId]);

  return (
    <>
      <GhostButton
        onClick={() => setOpen(true)}
        data-testid={`admin-comments-btn-${targetId}`}
        title="Discussion"
      >
        <MessageSquare className="size-3.5" />
        {count != null && count > 0 ? count : "Discussion"}
      </GhostButton>
      {open && (
        <Modal
          title={targetTitle ? `Discussion · ${targetTitle}` : "Discussion"}
          onClose={() => setOpen(false)}
          testId={`admin-comments-modal-${targetId}`}
        >
          {/* Force the inline panel to be pre-expanded inside the modal. */}
          <AdminCommentsPanel kind={kind} targetId={targetId} defaultOpen />
        </Modal>
      )}
    </>
  );
}
